import { LegacyRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { appAsset } from "../../app.asset";
import { InvoiceFormSchema } from "../../factory/form/form.rule";
import {
  GetPaymentModel,
  GetResponsePaymentModel,
} from "../../factory/model/query-model-payment";
import { GetResponseTravelVoucherModel } from "../../factory/model/query-model-travel-voucher";
import { useQyGetPayment } from "../../factory/query/payment.query";
import { useQyGetTravelVoucher } from "../../factory/query/travel-voucher.query";
import TextBlock from "../../shared/ui/text-block/text-block";
import { useDateTimeContext } from "../../shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "../../shared/utility/formats/currency-format";
import PreviewInputControl from "./preview-input-control";
import SummaryTable from "./summary-table";

interface InvoicePreviewFileProps {
  fileRef: LegacyRef<HTMLDivElement> | undefined;
  downloading?: boolean;
}
export function InvoicePreviewFile({ fileRef, downloading }: InvoicePreviewFileProps) {
  const { quotationId } = useParams();
  const { formatDate } = useDateTimeContext();
  const { watch, control, getValues } = useFormContext<InvoiceFormSchema>();
  const formData = watch();
  const quotationData = formData.quotation;
  const [payments, setPayments] = useState<GetPaymentModel[]>();
  const [totalPaid, setTotalPaid] = useState(0);
  // const [voucher, setVoucher] = useState("");
  const { invoiceId: voucher } = useParams();

  // useQyGetTravelVoucher(
  //   "",
  //   1,
  //   0,
  //   undefined,
  //   { base_quote: quotationId || "" },
  //   true,
  //   (response: GetResponseTravelVoucherModel) => {
  //     if (!response?.data) return;
  //     const voucherData = response?.data?.[0];
  //     setVoucher(voucherData.code);
  //   }
  // );

  useQyGetPayment(
    "",
    10,
    0,
    undefined,
    { voucher: voucher || "" },
    true,
    (response: GetResponsePaymentModel) => {
      if (!response || response.data?.length === 0) {
        return;
      }
      const total =
        response.data?.reduce((sum, p) => sum + (p.amount ?? 0), 0) || 0;
      setTotalPaid(total);
      setPayments(response?.data);
    }
  );

  const paymentMethods = [
    {
      id: 1,
      accountName: "HAPPY TRAILS TOURS AND TRAVEL",
      bank: "BDO Unibank, Inc.",
      accountNumber: "0078 1023 5386",
      swiftCode: "BNORPHMMXXX",
    },
    {
      id: 2,
      accountName: "NIKKI CHARMAINE C. REAMBONANZA",
      bank: "Bank of the Philippine Islands",
      accountNumber: "1199 5524 61",
      swiftCode: "BOPIPHMMXXX",
    },
    {
      id: 3,
      accountName: "NIKKI CHARMAINE C. REAMBONANZA",
      bank: "GCash",
      accountNumber: "0927 746 3991",
      swiftCode: "",
    },
  ];

  return (
    <div
      ref={fileRef}
      className="flex flex-col justify-between h-[14in] bg-white"
    >
      <div className="preview-file px-10 py-5 bg-white space-y-2">
        <section className="flex items-center justify-between mb-8">
          <div>
            <h1>TRAVEL</h1>
            <h1>INVOICE</h1>
          </div>
          <div>
            <img
              src={appAsset.previewLogo}
              alt="InvoicePreview Logo"
              className="w-full"
            />
          </div>
        </section>
        <section className="grid grid-cols-2">
          <div>
            <TextBlock label="Client Name" value={quotationData?.clientName} />
            <TextBlock
              label="Destination"
              value={quotationData?.locationName}
            />
            <p className={"text-gray-500 mb-0 text-sm"}>Travel Dates</p>
            <TextBlock
              label="Start of Service"
              value={formatDate(quotationData?.startDate)}
            />
            <TextBlock
              label="End of Service"
              value={formatDate(quotationData?.endDate)}
            />
          </div>
          <div>
            <TextBlock
              label="Invoice Date"
              value={formatDate(formData.invoiceDate)}
            />
            <TextBlock
              label="Package Type"
              value={quotationData?.packageName}
            />
          </div>
        </section>
        <section>
          <SummaryTable formData={quotationData || {}} downloading={downloading} isInvoice />
        </section>
        <section className="flex">
          <table className="flex-1 w-full text-[0.8em] border border-black">
            <thead>
              <tr className="bg-gray-900 text-white text-left font-bold">
                <th className="pl-4 py-1">TRAVEL DATE/S</th>
                <th className="py-1">DESCRIPTION</th>
                <th className="py-1">RATE PER PERSON</th>
                <th className="py-1">NO. OF PAX</th>
                <th className="py-1">AMOUNT</th>
              </tr>
            </thead>
            <tr>
              <td className="text-center">{`${formatDate(
                quotationData?.startDate
              )}`}</td>
              <td>
                {`${quotationData?.numDays}D${quotationData?.numNights}N ${quotationData?.packageName}`}
              </td>
              <td>
                {currencyFormat(
                  (quotationData?.amount || 0) /
                    ((quotationData?.numAdults || 0) +
                      (quotationData?.numChildren || 0))
                )}
              </td>
              <td>
                {(quotationData?.numAdults || 0) +
                  (quotationData?.numChildren || 0)}
              </td>
              <td className="text-right">
                {currencyFormat(quotationData?.amount || 0)}
              </td>
            </tr>
            {payments?.map((item) => (
              <tr key={item.id}>
                <td></td>
                <td>{item.payment_type}</td>
                <td></td>
                <td></td>
                <td className="text-right flex space-x-2">
                  <span>-</span>
                  <span>{currencyFormat(item.amount || 0)}</span>
                </td>
              </tr>
            ))}
            <tr>
              <td className="text-center">{`${formatDate(
                quotationData?.endDate
              )}`}</td>
              <td className="font-bold">
                BALANCE TO BE PAID UPON ARRIVAL (VIA CASH OR ONLINE PAYMENT)
              </td>
              <td></td>
              <td></td>
              <td className="text-right">
                {currencyFormat(
                  (quotationData?.amount || 0) - (totalPaid || 0) || 0
                )}
              </td>
            </tr>
          </table>
        </section>
        <section className="text-xs mb-5">
          <div className="font-bold">PACKAGE INCLUSIONS</div>
          <ul className="mb-2">
            {quotationData?.packageInclusions?.map((item, index) => (
              <li key={index}>
                <div>
                    <p className="font-normal text-black">
                      {item.value}
                    </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
        <section className="text-xs mb-5">
          <div className="font-bold">PAYMENT OPTIONS</div>
          <em>
            (Cash/check deposits, online bank transfers, and wire transfers)
          </em>
          <div className="my-5">
            {paymentMethods.map((item) => (
              <div className="mb-5" key={item.id}>
                <div className="grid grid-cols-3">
                  <p className="">Account Name</p>
                  <p>: {item?.accountName}</p>
                  <p></p>
                </div>
                <div className="grid grid-cols-3">
                  <p className="">Bank</p>
                  <p>: {item?.bank}</p>
                  <p></p>
                </div>
                <div className="grid grid-cols-3">
                  <p className="">Account Number</p>
                  <p>: {item?.accountNumber}</p>
                  <p></p>
                </div>
                <div className="grid grid-cols-3">
                  <p className="">Swift Code</p>
                  <p>: {item?.swiftCode}</p>
                  <p></p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="text-xs mb-5">
          <div className="font-bold">GENERAL TERMS AND CONDITIONS</div>
          <ol className="list-decimal pl-4">
            <li>
              Kindly settle the downpayment{" "}
              <span className="text-orange-400 font-bold">
                <PreviewInputControl
                  placeholder="on or before (input date)"
                  control={control}
                  name="dueDate"
                  type="date"
                />
              </span>{" "}
              to confirm the booking.
            </li>
            <p>
              Balance may be paid upon arrival in Bohol in cash or via online
              payment.
            </p>
            <li>
              All hotel and/or tour schedules are arranged ahead of time, thus
              down payment is strictly non-refundable.
            </li>
            <li>
              No refund will be given for any portions of the tour unused by the
              Client regardless of circumstances.
            </li>
            <li>
              Guests may reschedule day tours if the request is made at least
              one week before the start of the original tour date.{" "}
            </li>
            <p>
              For accommodation rebooking, the Hotel's terms and conditions will
              apply.
            </p>
            <li>
              In the event of natural calamities, force majeure, or
              government-mandated cancellations, day tours can be subject to
              rebooking or a refund, less expenses incurred in preparation for
              the tour. Rebooking is permitted within one year from the original
              tour date. Accomodation rebooking is always subject to Hotel's
              terms and conditions.
            </li>
          </ol>
        </section>
        <section className="text-xs my-5">
          <p>Issued by:</p>
          <div className="mt-10 text-xs">
            <p className="text-md">NIKKI CHARMAINE REAMBONANZA-ENERIO</p>
            <p className="font-bold">Happy Trails Tours and Travel</p>
            <p>DTI No. 4386732</p>
            <p>Business Permit No. BP2025-01468-0</p>
            <p>DOT Accreditation No. DOT-R07-TTA-03528-2024</p>
          </div>
        </section>
      </div>

      <div>
        <img
          src={appAsset.previewFooter}
          alt="InvoicePreview Footer"
          className="w-full py-2 bg-[#262626]"
        />
      </div>
    </div>
  );
}

export default InvoicePreviewFile;
